import { Dispatch, MutableRefObject, SetStateAction, useContext } from "react";

import { Tree } from "components/objectTree/types";
import { ModelTreeNodeData } from "features/modelTree/types";

import { PreloadedTreeContext, PreloadedTreeRootReadyContext, SetTreeContext, TreeContext } from "./context";

export function useObjectTree(): Tree<ModelTreeNodeData> {
    return useContext(TreeContext);
}

/**
 * Returns the ref holding the background-preloaded object tree (top categories,
 * plus deeper folders loaded during idle time), or `null` outside the provider.
 * Read `.current` non-reactively to seed the Object Tree so it appears instantly
 * on first open; `.current` is `null` until the root preload completes.
 */
export function usePreloadedObjectTreeRef(): MutableRefObject<Tree<ModelTreeNodeData> | null> | null {
    return useContext(PreloadedTreeContext);
}

/**
 * Reactive flag that flips to `true` once (and stays `true`) the moment the
 * background preload's root level finishes loading. Use this to know *when* it
 * becomes safe/worthwhile to (re-)read {@link usePreloadedObjectTreeRef}, since
 * the ref itself doesn't trigger re-renders — see {@link PreloadedTreeRootReadyContext}.
 */
export function usePreloadedObjectTreeRootReady(): boolean {
    return useContext(PreloadedTreeRootReadyContext);
}

export function useSetObjectTree(): Dispatch<SetStateAction<Tree<ModelTreeNodeData>>> {
    const context = useContext(SetTreeContext);

    if (context === undefined) {
        throw new Error("useSetObjectTree must be used within an ObjectTreeProvider");
    }

    return context;
}
